import React, { useEffect, useState } from 'react';
import { DateRange } from 'react-date-range';
import { Calendar } from 'react-date-range';
import { enUS } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useParams, useNavigate } from 'react-router-dom';
import supabase from '../config/supabaseClient';
import '../components/eventeditor.css';
import { QRCodeSVG } from 'qrcode.react';
import EventViewer from './eventviewer'; // adjust path if needed
import Handshake from './handshake';
import GuestTable from './guesttable';
import SaveIcon from '../icons/saveicon';
import GuestListIcon from '../icons/guestlisticon';
import BuddyIcon from '../icons/buddyicon';
import DashboardIcon from '../icons/dashboardicon';
import SettingsIcon from '../icons/settingsicon';
import BackToViewerIcon from '../icons/backtoviewericon';
import TrashEventIcon from '../icons/trasheventicon';
import QRCodeIcon from '../icons/qrcodeicon';
import '../components/eventviewer.css';
import OpenInviteIcon from '../icons/openinviteicon';
import { usePassport } from '../passport';


console.log('EventViewer is:', EventViewer);

export default function EventEditor() {
  const { user, profile } = usePassport();
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Controlled input states
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [date, setDate] = useState(new Date());
  const [weather, setWeather] = useState('');
  const [whatToBring, setWhatToBring] = useState('');
  const [links, setLinks] = useState('');
  const [moderatorContact, setModeratorContact] = useState('');
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [tempImageURL, setTempImageURL] = useState('');
  const [showQRCodePopup, setShowQRCodePopup] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showGuestTable, setShowGuestTable] = useState(false);
  const [allowGuestInvites, setAllowGuestInvites] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [customField1Label, setCustomField1Label] = useState('Custom Field 1');
  const [customField2Label, setCustomField2Label] = useState('Custom Field 2');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);


  function CharacterLimitModal({ onClose }) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <h3>Too Many Characters</h3>
          <p>There's a 40 character limit. For additional details, please use the Event Details Box.</p>
          <button onClick={onClose}>OK</button>
        </div>
      </div>
    );
  }

    useEffect(() => {
      const fetchEvent = async () => {
        const { data, error } = await supabase
          .from('hosting')
          .select('*')
          .eq('id', id)
          .single();

        if (error) {
          console.error('Error fetching event:', error);
          setLoading(false);
        } else {
          const normalized = {
            ...data,
            event_link: Array.isArray(data.event_link)
              ? data.event_link
              : typeof data.event_link === 'string' && data.event_link.startsWith('[')
              ? JSON.parse(data.event_link)
              : [],
          };

          setEvent(normalized);
          setLinks(normalized.event_link);
          setLoading(false);

          setDescription(data.description || '');
          setLocation(data.event_location || '');
          setDate(data.event_date ? new Date(data.event_date) : new Date());
          setWeather(data.weather_link || '');
          setWhatToBring(data.what_to_bring || '');
          setModeratorContact(data.moderator_contact || '');
          setAllowGuestInvites(data.allow_guest_invites ?? false);
          setCustomField1Label(data.custom_field_1_label || '');
          setCustomField2Label(data.custom_field_2_label || '');
          setIsPublic(normalized.is_public || false);
        }
      };

      fetchEvent();
    }, [id]);


  if (loading) return <div className="eventeditor-loading">Loading event...</div>;
  if (!event) return <div className="eventeditor-error">Event not found.</div>;

const geocodeLocation = async (locationString) => {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(locationString)}&format=json&limit=1`
  );
  const data = await response.json();
  if (data && data.length > 0) {
    return {
      lat: parseFloat(data[0].lat),
      lon: parseFloat(data[0].lon),
    };
  } else {
    return null;
  }
};

    const handleSave = async () => {
      if (!event) return;
    
    const coords = await geocodeLocation(location);

      const updatedEvent = {
        event_location: event.event_location || '',
        event_date: date ? date.toISOString() : '',
        weather_link: event.weather_link || '',
        what_to_bring: event.what_to_bring || '',
        event_link: Array.isArray(links) && links.length > 0 ? JSON.stringify(links) : JSON.stringify([]),
        moderator_contact: event.moderator_contact || '',
        description: event.description || '',
        allow_guest_invites: allowGuestInvites,
        event_name: event.event_name || '',
        event_image: event.event_image || '',
        custom_field_1: event.custom_field_1 || '',
        custom_field_2: event.custom_field_2 || '',
        custom_field_1_label: event.custom_field_1_label || '',
        custom_field_2_label: event.custom_field_2_label || '',
        event_lat: coords?.lat || null,
        event_lon: coords?.lon || null,
      };

      const { error } = await supabase
        .from('hosting')
        .update(updatedEvent)
        .eq('id', event.id);

      if (error) {
        alert('Error saving changes: ' + error.message);
        console.error('Save error:', error);
      } else {
        const { data: refreshed, error: refetchError } = await supabase
          .from('hosting')
          .select('*')
          .eq('id', event.id)
          .single();

        if (refetchError) {
          console.error('Refetch error:', refetchError);
          alert('Event saved, but failed to refresh data.');
        } else {
          setEvent(refreshed);
        }

        alert('Event updated successfully!');
      }
    };
  

  const handleDelete = async () => {
    const confirm = window.confirm("Are you sure you want to delete this event?");
    if (!confirm) return;

    const { error } = await supabase
      .from('hosting')
      .delete()
      .eq('id', event.id);

    if (error) {
      alert("Error deleting event: " + error.message);
      console.error("Delete error:", error);
    } else {
      alert("Event deleted.");
      navigate('/dashboard');
    }
  };

  console.log('showDatePicker is:', showDatePicker);
  console.log('typeof date:', typeof date, '| value:', date);

  const updatePublicStatus = async (makePublic) => {
  const { error } = await supabase
    .from('hosting')
    .update({ is_public: makePublic })
    .eq('id', event.id);

  if (error) {
    alert(`Error updating visibility: ${error.message}`);
    console.error(error);
    return;
  }

  // Refetch updated event
  const { data: refreshed, error: refetchError } = await supabase
    .from('hosting')
    .select('*')
    .eq('id', event.id)
    .single();

  if (refetchError) {
    alert('Updated visibility, but failed to refresh event data.');
    console.error(refetchError);
  } else {
    setEvent(refreshed);
    setIsPublic(refreshed.is_public); // 🔥 This makes the globe update!
  }

  setShowModal(false); // Close modal after update
};


function LinkModal({ links, setLinks, onClose }) {
  const handleLinkChange = (index, field, value) => {
    const updated = [...links];
    updated[index][field] = value;
    setLinks(updated);
  };

  const addLink = () => {
    if (links.length < 4) {
      setLinks([...links, { label: '', url: '' }]);
    }
  };

  const removeLink = (index) => {
    const updated = [...links];
    updated.splice(index, 1);
    setLinks(updated);
  };


    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <h3>Event Hyperlinks</h3>
          {links.map((link, idx) => (
            <div key={idx} style={{ marginBottom: '1rem' }}>
              <input
                type="text"
                placeholder="Label (e.g., Ticket Info)"
                value={link.label}
                onChange={(e) => handleLinkChange(idx, 'label', e.target.value)}
                style={{ width: '45%', marginRight: '10px' }}
              />
              <input
                type="text"
                placeholder="URL"
                value={link.url}
                onChange={(e) => handleLinkChange(idx, 'url', e.target.value.slice(0, 40))}
                style={{ width: '45%' }}
              />
              <button onClick={() => removeLink(idx)}>❌</button>
            </div>
          ))}

          {links.length < 4 && (
            <button onClick={addLink} style={{ marginRight: '10px' }}>
              ➕ Add Link
            </button>
          )}
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

   return (
  <div className="eventeditor-container">
    <>
      <div className="eventeditor-title-edit">
        <label>Edit Event Title:</label>
        <input
          type="text"
          value={event.event_name}
          onChange={(e) =>
            setEvent((prev) => ({ ...prev, event_name: e.target.value }))
          }
        />
      </div>

      <div className="eventeditor-layout">
        {/* Left Side */}
        <div className="editor-left">
          {/* Event Image */}
          <div className="image-wrapper">
            <img
              src={event.event_image}
              alt={event.event_name}
              className="eventeditor-image"
              onClick={() => setShowImageEditor(true)}
            />
            <div className="image-overlay" onClick={() => setShowImageEditor(true)}>
              Change Image?
            </div>
          </div>

          {/* Image Edit Modal */}
          {showImageEditor && (
            <div className="image-edit-popup">
              <input
                type="text"
                placeholder="Paste new image URL"
                value={tempImageURL}
                onChange={(e) => setTempImageURL(e.target.value)}
              />
              <div className="image-edit-buttons">
                <button
                  onClick={() => {
                    setEvent((prev) => ({ ...prev, event_image: tempImageURL }));
                    setShowImageEditor(false);
                  }}
                >
                  Update Image
                </button>
                <button onClick={() => setShowImageEditor(false)}>Cancel</button>
              </div>
            </div>
          )}

          {/* ✅ Event Summary */}
          <h3><u>Event Summary</u></h3>
          <Handshake
            event={event}
            setEvent={setEvent}
            isEditor={true}
            showHeading={false}
            overrideDateField={
              <div className="field-block">
                <label className="field-label">
                  <b>Date:</b>
                </label>
                <input
                  type="text"
                  readOnly
                  className="date-input"
                  value={
                    date instanceof Date && !isNaN(date)
                      ? date.toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'TBD'
                  }
                  onClick={() => setShowDatePicker(true)}
                />
                {showDatePicker && (
                  <div className="calendar-popover">
                    <div className="calendar-header">Event Date</div>
                    <Calendar
                      date={date}
                      onChange={(selected) => {
                        setDate(selected);
                        setShowDatePicker(false);
                      }}
                      locale={enUS}
                      minDate={new Date()}
                    />
                  </div>
                )}
              </div>
            }
            customLabels={{
              custom_field_1: customField1Label,
              custom_field_2: customField2Label,
            }}
            setShowLinkModal={setShowLinkModal}
          />
        </div>

        {/* Right Side */}
        <div className="editor-right">
          <div className="floating-icons">
            <button
                className="topbar-icon-button"
                onClick={() => setShowModal(true)}
                title={isPublic ? "Event is Public" : "Make Public"}
                style={{
                  position: 'relative',
                  width: 32,
                  height: 32,
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Blue background only when public */}
                {isPublic && (
                  <div
                    style={{
                      position: 'absolute',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      backgroundColor: 'lightgreen',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 0,
                    }}
                  />
                )}

                {/* Icon on top */}
                <div style={{ zIndex: 1 }}>
                  <OpenInviteIcon size={24} stroke="white" />
                </div>
              </button>


            <button className="topbar-icon-button" onClick={() => navigate('/dashboard')} title="Dashboard">
              <DashboardIcon size={28} />
            </button>
            <button className="topbar-icon-button" onClick={() => setShowQRCodePopup(true)} title="QR Code">
              <QRCodeIcon size={28} />
            </button>
            <button className="topbar-icon-button" onClick={() => navigate(`/event/${event.id}`)} title="Back to Event">
              <BackToViewerIcon size={28} />
            </button>
            <button className="topbar-icon-button" onClick={() => setShowGuestTable(true)} title="Guest Table">
              <GuestListIcon size={28} />
            </button>
            <button className="topbar-icon-button" onClick={() => alert("Settings")} title="Settings">
              <SettingsIcon size={28} />
            </button>
            <button className="topbar-icon-button" onClick={() => alert("Coming Soon!")} title="Ask Buddy">
              <BuddyIcon size={28} />
            </button>
          </div>

          <div className="editor-right-content">
            <div className="guest-invite-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={allowGuestInvites}
                  onChange={(e) => setAllowGuestInvites(e.target.checked)}
                />
                Allow guests to invite others
              </label>
            </div>

            <div className="text-save-wrapper">
              <div className="details-box-header">
                <span className="details-title">Event Details</span>
                {event.vrc && <span className="vrc-code-display">VRC: {event.vrc}</span>}
              </div>

              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Additional event details can go here..."
                className="eventeditor-textarea"
              />

              <div className="save-button-inline">
                <button
                  className="topbar-icon-button"
                  title="Save Changes"
                  onClick={handleSave}
                >
                  <SaveIcon size={28} />
                </button>

                <button
                  className="topbar-icon-button"
                  title="Delete Event"
                  onClick={handleDelete}
                >
                  <TrashEventIcon size={28} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Popup */}
      {showQRCodePopup && (
        <div className="qr-popup-overlay" onClick={() => setShowQRCodePopup(false)}>
          <div className="qr-popup-content" onClick={(e) => e.stopPropagation()}>
            <QRCodeSVG value={`https://yourdomain.com/qrcodeinvite/${event.id}`} size={220} />
            <div style={{ marginTop: '10px', fontSize: '0.9rem' }}>{event.event_name}</div>
            <button onClick={() => setShowQRCodePopup(false)} style={{ marginTop: '10px' }}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Guest Table Popup */}
        {showGuestTable && user && profile && (
          <GuestTable
            eventId={event.id}
            eventTitle={event.event_name}
            vrc={event.vrc}
            onClose={() => setShowGuestTable(false)}
            viewerRole="host" // this can stay for now
            user={user}
            profile={profile}
          />
        )}

      {showLinkModal && (
        <div className="modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Do you have any relevant hyperlinks you'd like to share?</h3>
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className="link-entry-row">
                <input
                  type="text"
                  placeholder="Hyperlink Name"
                  value={links[index]?.label || ''}
                  onChange={(e) => {
                    const updated = [...links];
                    updated[index] = {
                      ...updated[index],
                      label: e.target.value,
                      url: updated[index]?.url || '',
                    };
                    setLinks(updated);
                  }}
                />
                <input
                  type="text"
                  placeholder="Hyperlink URL"
                  value={links[index]?.url || ''}
                  onChange={(e) => {
                    const updated = [...links];
                    updated[index] = {
                      ...updated[index],
                      url: e.target.value.slice(0, 40),
                      label: updated[index]?.label || '',
                    };
                    setLinks(updated);
                  }}
                />
              </div>
            ))}
            <button
              onClick={() => {
                const filtered = links.filter(link => link.label && link.url);
                setEvent((prev) => ({
                  ...prev,
                  event_link: filtered,
                }));
                setShowLinkModal(false);
              }}
            >
              Save Links
            </button>
            <button onClick={() => setShowLinkModal(false)}>Cancel</button>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {isPublic ? (
              <>
                <h3>This event is already public.</h3>
                <p>Would you like to keep it public or make it private?</p>
                <button onClick={() => updatePublicStatus(false)}>Make Private</button>
                <button onClick={() => setShowModal(false)}>Keep Public</button>
              </>
            ) : (
              <>
                <h3>Are you sure you want to make this event public?</h3>
                <p>This event will be visible to the public, including non-members.</p>
                <button onClick={() => updatePublicStatus(true)}>Yes, Make Public</button>
                <button onClick={() => setShowModal(false)}>Cancel</button>
              </>
            )}
          </div>
        </div>
      )}

    </>
  </div>
);
}